<template>
  <v-card :loading="form.processing" flat>
    <v-card-title>
      <slot name="header"></slot>
    </v-card-title>
    <v-card-text>
      <v-form ref="form" class="mt-4" :disabled="isReadonly || form.processing">
        <div class="label mb-2">Teacher</div>
        <v-autocomplete
          v-model="form.teacher_id"
          :items="teachers"
          :rules="[validationRules.required]"
          :error="form.errors && form.errors.teacher_id"
          :error-messages="form.errors.teacher_id"
          :loading="isTeacherListLoading"
          item-text="name"
          item-value="id"
          solo
          flat
          outlined
          dense
          placeholder="Choose teacher"
          @change="changeTeacher"
        />
        <div class="label mb-2">Period</div>
        <v-menu
          ref="periodMenu"
          v-model="periodMenu"
          transition="scale-transition"
          offset-y
          max-width="300px"
          min-width="auto"
        >
          <template #activator="{ on, attrs }">
            <v-text-field
              v-model="period"
              label="Period"
              prepend-icon="mdi-calendar"
              readonly
              outlined
              solo
              flat
              background-color="white"
              dense
              v-bind="attrs"
              v-on="on"
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="period"
            type="month"
            scrollable
            @change="changePeriod"
          >
          </v-date-picker>
        </v-menu>
        <div v-if="selectedTeacher && selectedTeacher.is_native_speaker">
          <div class="label mb-2">
            Currency Exchange Rate ({{ form.currency_code }} to IDR)
          </div>
          <v-text-field
            v-model="form.currency_exchange_rate"
            prefix="IDR"
            class="input-right"
            solo
            flat
            outlined
            dense
            placeholder="Currency exchange rate"
          >
          </v-text-field>
        </div>
        <v-card
          :loading="isClassroomListLoading || isClassScheduleListLoading"
          flat
        >
          <v-card-title>Classes</v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-row v-if="!$vuetify.breakpoint.mobile">
              <v-col cols="2"><div class="label">Class Name</div></v-col>
              <v-col cols="3">
                <v-row>
                  <v-col>
                    <div class="label">Class Date</div>
                  </v-col>
                  <v-col>
                    <div class="label">Class Type</div>
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="2"><div class="label">Students</div></v-col>
              <v-col cols="1">
                <div class="label text-center">Total Hours</div>
              </v-col>
              <v-col cols="2"
                ><div class="label text-right">Hourly Rate</div></v-col
              >
              <v-col cols="2">
                <div class="label text-right">Total Hourly Amount</div>
              </v-col>
            </v-row>
            <v-card
              v-for="(item, i) in form.items"
              :key="i"
              tile
              outlined
              class="my-4"
            >
              <v-container fluid>
                <v-row align="baseline">
                  <v-col cols="12" md="2">
                    <div v-if="$vuetify.breakpoint.mobile" class="label mb-2">
                      Class Name
                    </div>
                    <h4>
                      {{ item.classroom.name }}
                      ({{
                        `${item.classroom.subscription_package.class_per_week} x ${item.classroom.subscription_package.minutes_per_class} mins`
                      }})
                    </h4>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-row>
                      <v-col cols="12" md="6">
                        <div
                          v-if="$vuetify.breakpoint.mobile"
                          class="label mb-2"
                        >
                          Class Date
                        </div>
                        <div>
                          {{
                            formatDate(item.classroom.start_date, 'DD MMM YYYY')
                          }}
                          -
                          {{
                            formatDate(item.classroom.end_date, 'DD MMM YYYY')
                          }}
                        </div>
                      </v-col>
                      <v-col cols="12" md="6">
                        <div
                          v-if="$vuetify.breakpoint.mobile"
                          class="label mb-2"
                        >
                          Class Type
                        </div>
                        <div>
                          {{
                            classroomTypeProps(
                              item.classroom.class_type,
                              'text'
                            )
                          }}
                        </div>
                      </v-col>
                    </v-row>
                  </v-col>
                  <v-col cols="12" md="2">
                    <div v-if="$vuetify.breakpoint.mobile" class="label mb-2">
                      Students
                    </div>
                    <ul>
                      <li
                        v-for="(student, j) in item.classroom.students"
                        :key="j"
                      >
                        {{ student.name }}
                      </li>
                    </ul>
                  </v-col>
                  <v-col cols="12" md="1">
                    <div v-if="$vuetify.breakpoint.mobile" class="label mb-2">
                      Total Hours
                    </div>
                    <div
                      :class="{
                        'text-center': !$vuetify.breakpoint.mobile,
                      }"
                    >
                      <div>{{ item.total_hours }}</div>
                    </div>
                  </v-col>
                  <v-col cols="12" md="2">
                    <div v-if="$vuetify.breakpoint.mobile" class="label mb-2">
                      Hourly Rate
                    </div>
                    <v-text-field
                      v-model="item.hourly_rate"
                      :rules="[validationRules.required]"
                      class="input-right"
                      :prefix="form.currency_code"
                      solo
                      flat
                      outlined
                      dense
                      @change="calculateItem(i)"
                    />
                  </v-col>
                  <v-col cols="12" md="2">
                    <div v-if="$vuetify.breakpoint.mobile" class="label mb-2">
                      Total Hourly Amount
                    </div>
                    <v-text-field
                      :value="formatNumber(item.total_hourly_amount)"
                      class="input-right"
                      :prefix="form.currency_code"
                      solo
                      flat
                      outlined
                      dense
                      readonly
                    />
                  </v-col>
                </v-row>
              </v-container>
              <v-divider></v-divider>
              <v-container fluid class="grey lighten-4">
                <v-row
                  v-for="schedule in groupedClassSchedules[item.classroom_id]"
                  :key="`detail-${schedule.id}`"
                >
                  <v-col cols="6" md="2">
                    {{ formatDate(schedule.class_date, 'DD MMM YYYY') }}
                  </v-col>
                  <v-col cols="6" md="2">
                    {{ formatTime(schedule.start_time) }} -
                    {{ formatTime(schedule.end_time) }}
                  </v-col>
                </v-row>
              </v-container>
            </v-card>
            <v-row>
              <v-col
                cols="12"
                md="10"
                :class="{
                  'text-right': !$vuetify.breakpoint.mobile,
                }"
              >
                <div class="label">Total Salary Amount</div>
              </v-col>
              <v-col
                cols="12"
                md="2"
                :class="{
                  'text-right': !$vuetify.breakpoint.mobile,
                }"
              >
                <div class="label d-flex justify-space-between">
                  <div>{{ form.currency_code }}</div>
                  <div>{{ formatNumber(form.total_hourly_amount) }}</div>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
        <div class="label mb-2">Status</div>
        <v-select
          v-model="form.status"
          :items="statusOptions"
          :rules="[validationRules.required]"
          :error="form.errors.status"
          :error-messages="form.errors.status"
          solo
          flat
          outlined
          dense
          placeholder="Select payroll status"
        />
        <v-expand-transition>
          <div v-show="form.status === 'PAID'">
            <div class="label mb-2">Paid Date</div>
            <v-menu
              ref="paidDateMenu"
              v-model="paidDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  v-model="form.paid_date"
                  placeholder="Select paid date"
                  prepend-icon="mdi-calendar"
                  solo
                  flat
                  outlined
                  dense
                  readonly
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="form.paid_date"
                @change="$refs.paidDateMenu.save(form.paid_date)"
              ></v-date-picker>
            </v-menu>
          </div>
        </v-expand-transition>
      </v-form>
    </v-card-text>
    <v-card-actions>
      <v-container fluid>
        <v-row justify="end">
          <v-col md="3" cols="12">
            <v-btn block depressed outlined @click="cancel">Cancel</v-btn>
          </v-col>
          <v-col md="3" cols="12">
            <v-btn
              block
              depressed
              color="primary"
              :disabled="isReadonly || form.processing"
              @click="submit"
            >
              Save Changes
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-card-actions>
  </v-card>
</template>
<script>
import collect from 'collect.js'
import { mapGetters, mapActions } from 'vuex'
import constant from '@/constant'
export default {
  name: 'PayrollForm',
  props: {
    mode: {
      type: String,
      required: true,
      validator: () => ['add', 'edit', 'view'],
    },
    payroll: {
      type: Object,
      default: null,
      required: false,
    },
  },
  data() {
    return {
      periodMenu: false,
      period: null,
      paidDateMenu: false,
      form: this.$inertia.form({
        teacher_id: null,
        month: null,
        year: null,
        paid_date: null,
        notes: null,
        currency_code: null,
        currency_exchange_rate: null,
        total_hourly_amount: 0.0,
        status: null,
        items: [],
      }),
    }
  },
  computed: {
    ...mapGetters({
      isLoading: 'payroll/isDetailLoading',
      teacherList: 'teacher/list',
      isTeacherListLoading: 'teacher/isListLoading',
      classroomList: 'classroom/list',
      isClassroomListLoading: 'classroom/isListLoading',
      classTotalHours: 'classroom/totalHours',
      classScheduleList: 'classroomSchedule/list',
      isClassScheduleListLoading: 'classroomSchedule/isListLoading',
    }),
    isReadonly() {
      return this.mode === 'view'
    },
    teachers() {
      return this.teacherList.data
    },
    statusOptions() {
      return constant.payrollStatusOptions
    },
    selectedTeacher() {
      return this.form.teacher_id
        ? this.teachers.find((e) => e.id === this.form.teacher_id)
        : null
    },
    classrooms() {
      return this.classroomList.data
    },
    classSchedules() {
      return this.classScheduleList.data
    },
    groupedClassSchedules() {
      return this.classSchedules.length > 0
        ? collect(this.classSchedules).groupBy('classroom_id').all()
        : {}
    },
    hourlyRateOptions() {
      return this.selectedTeacher
        ? [
            {
              text: 'Private',
              key: 'PRIVATE',
              value: this.selectedTeacher.private_hourly_rate,
            },
            {
              text: 'Small Group',
              key: 'SMALL_GROUP',
              value: this.selectedTeacher.small_group_hourly_rate,
            },
            {
              text: 'Large Group',
              key: 'LARGE_GROUP',
              value: this.selectedTeacher.large_group_hourly_rate,
            },
            {
              text: 'Group 2',
              key: 'GROUP_2',
              value: this.selectedTeacher.group_2_hourly_rate,
            },
            {
              text: 'Group 3',
              key: 'GROUP_3',
              value: this.selectedTeacher.group_3_hourly_rate,
            },
            {
              text: 'Group 4',
              key: 'GROUP_4',
              value: this.selectedTeacher.group_4_hourly_rate,
            },
            {
              text: 'Group 5',
              key: 'GROUP_5',
              value: this.selectedTeacher.group_5_hourly_rate,
            },
            {
              text: 'Group 6',
              key: 'GROUP_6',
              value: this.selectedTeacher.group_6_hourly_rate,
            },
            {
              text: 'Custom 1',
              key: 'CUSTOM_1',
              value: this.selectedTeacher.custom_1_hourly_rate,
            },
            {
              text: 'Custom 2',
              key: 'CUSTOM_2',
              value: this.selectedTeacher.custom_2_hourly_rate,
            },
            {
              text: 'Custom 3',
              key: 'CUSTOM_3',
              value: this.selectedTeacher.custom_3_hourly_rate,
            },
            {
              text: 'Custom 4',
              key: 'CUSTOM_4',
              value: this.selectedTeacher.custom_4_hourly_rate,
            },
          ]
        : []
    },
  },
  watch: {
    $props: {
      immediate: true,
      handler() {
        this.validateProps()
      },
    },
  },
  created() {
    this.init()
  },
  methods: {
    ...mapActions({
      getTeacherList: 'teacher/getList',
      getClassroomList: 'classroom/getList',
      getClassTotalHours: 'classroom/getTotalHours',
      getClassScheduleList: 'classroomSchedule/getList',
    }),
    validateProps() {
      if ((this.mode === 'edit' || this.mode === 'view') && !this.payroll) {
        console.error(
          `payroll props must be provided for 'edit' and 'view' mode`
        )
      }
    },
    async init() {
      await this.getTeacherList()
      if (this.mode != 'add' && this.payroll != null) {
        _.merge(this.form, this.payroll)
        this.period = moment({
          year: this.payroll.year,
          month: this.payroll.month - 1,
        }).format('YYYY-MM')
        await this.fetchClassSchedules()
      }
    },
    async changeTeacher(value) {
      const teacher = this.teachers.find((e) => e.id === value)
      if (teacher) {
        this.form.currency_code = teacher.currency_code
        if (this.period) {
          await this.fetchClassSchedules()
        }
      }
    },
    async changePeriod(value) {
      this.$refs.periodMenu.save(value)
      const dt = moment(value, 'YYYY-MM')
      this.form.year = dt.year()
      this.form.month = dt.month() + 1
      if (this.selectedTeacher) {
        await this.fetchClassSchedules()
      }
    },
    async fetchClassSchedules() {
      const dt = moment(this.period, 'YYYY-MM')
      const startDate = dt.clone().startOf('month').format('YYYY-MM-DD')
      const endDate = dt.clone().endOf('month').format('YYYY-MM-DD')
      await this.getClassroomList({
        teacher_id: this.selectedTeacher.id,
      })
      const classroomIds = this.classrooms.map((e) => e.id)
      await this.getClassScheduleList({
        classroom_id: classroomIds,
        class_date: [startDate, endDate],
        is_completed: 1,
        sort: ['class_date,asc'],
      })
      const groupedClassSchedules = collect(this.classSchedules)
        .groupBy('classroom_id')
        .all()
      for (const classroom of this.classrooms) {
        const schedules = groupedClassSchedules[classroom.id]
        if (!schedules || schedules.length == 0) {
          continue
        }

        const tempTotalHours = schedules.reduce((total, schedule) => {
          const startTime = moment(
            `${schedule.class_date}T${schedule.start_time}`
          )
          const endTime = moment(`${schedule.class_date}T${schedule.end_time}`)
          const hours = endTime.diff(startTime, 'hours', true)
          return total + hours
        }, 0.0)
        const totalHours = parseFloat(tempTotalHours.toFixed(2))
        const hourlyRate =
          this.hourlyRateOptions.find((e) => e.key === classroom.class_type)
            ?.value ?? 0.0

        const itemIndex = this.form.items.findIndex(
          (e) => e.classroom_id === classroom.id
        )
        if (itemIndex !== -1) {
          continue
        }
        const item = {
          classroom_id: classroom.id,
          classroom: classroom,
          hourly_rate: hourlyRate,
          total_hours: totalHours,
          total_hourly_amount: totalHours * hourlyRate,
        }
        this.form.items.push(item)
      }
      this.calculatePayroll()
    },
    calculateItem(index) {
      const item = this.form.items[index]
      if (item) {
        item.total_hourly_amount = item.total_hours * item.hourly_rate
      }
      this.calculatePayroll()
    },
    calculatePayroll() {
      let totalHourlyAmount = 0
      for (const item of this.form.items) {
        totalHourlyAmount += item.total_hourly_amount
      }
      this.form.total_hourly_amount = totalHourlyAmount
    },
    cancel() {
      this.$emit('cancel')
    },
    async submit() {
      if (this.$refs.form.validate()) {
        // const dt = moment(this.period, 'YYYY-MM')
        // this.form.year = dt.year()
        // this.form.month = dt.month()
        if (this.mode === 'add') {
          this.form.post('/payrolls')
        } else if (this.mode === 'edit') {
          this.form.put(`/payrolls/${this.payroll.id}`)
        }
      }
    },
  },
}
</script>
<style lang="scss" scoped>
.input-right {
  :deep(input) {
    text-align: right;
  }
}
</style>
